/**
 * <ds-lazy-video> — viewport-aware lazy loading for the DigiTapes design
 * system's video slots (see snippets/ds-media.liquid).
 *
 * The <source> elements inside carry `data-src` instead of `src`, so the
 * browser's HTML parser never fetches video bytes on page load. An
 * IntersectionObserver swaps in the real `src` only once the element is
 * about to enter the viewport (a generous rootMargin gives it time to
 * buffer before the visitor actually reaches it). Autoplay videos
 * (`data-autoplay`) additionally play() once visible and pause() once they
 * scroll out, so a page with many autoplay clips isn't decoding video the
 * visitor can't see.
 */
class DsLazyVideo extends HTMLElement {
  connectedCallback() {
    const video = this.querySelector('video');
    if (!video) return;

    this.video = video;
    this.autoplay = this.hasAttribute('data-autoplay');
    this.loaded = false;

    if ('IntersectionObserver' in window) {
      this.observer = new IntersectionObserver(this.onIntersect.bind(this), {
        rootMargin: '300px 0px',
        threshold: 0,
      });
      // Observe the <video> itself, not this wrapper: the wrapper is
      // `display: contents` (see .contents in base.css) so it has no layout
      // box of its own, and browsers never report a boxless element as
      // intersecting — which silently prevented sources from ever loading.
      this.observer.observe(this.video);
    } else {
      // No IntersectionObserver support: fall back to loading immediately
      // rather than never loading at all.
      this.loadSources();
      if (this.autoplay) video.play().catch(() => {});
    }
  }

  disconnectedCallback() {
    this.observer?.disconnect();
  }

  onIntersect(entries) {
    for (const entry of entries) {
      if (entry.isIntersecting) {
        this.loadSources();
        if (this.autoplay) this.video.play().catch(() => {});
      } else if (this.autoplay && this.loaded) {
        this.video.pause();
      }
    }
  }

  loadSources() {
    if (this.loaded) return;
    this.loaded = true;

    const sources = this.querySelectorAll('source[data-src]');
    for (const source of sources) {
      source.setAttribute('src', source.dataset.src);
      source.removeAttribute('data-src');
    }
    this.video.load();
  }
}

if (!customElements.get('ds-lazy-video')) {
  customElements.define('ds-lazy-video', DsLazyVideo);
}
